import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, DollarSign, FileText, User } from 'lucide-react';
import LoadingSpinner from '../components/common/LoadingSpinner';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const InvoiceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchInvoiceDetails();
  }, [id]);

  const fetchInvoiceDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API_URL}/invoices/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoice(response.data?.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch invoice details');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      unpaid: 'bg-yellow-100 text-yellow-800',
      partially_paid: 'bg-blue-100 text-blue-800',
      paid: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800',
      written_off: 'bg-gray-100 text-gray-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const isAdmin =
    user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'system_admin';

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error || !invoice) {
    return (
      <div className="p-4 md:p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error || 'Invoice not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/accounting/invoices')}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Invoice {invoice.invoice_number}</h1>
            <p className="text-sm text-gray-500">Customer: {invoice.customer_name}</p>
          </div>
        </div>
        <span
          className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${getStatusBadge(invoice.status)}`}
        >
          {invoice.status ? invoice.status.replace('_', ' ').toUpperCase() : 'UNKNOWN'}
        </span>
      </div>

      {/* Invoice Information */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-4">Invoice Information</h3>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm text-gray-500">Invoice Number</dt>
                <dd className="text-sm font-medium text-gray-900">{invoice.invoice_number}</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Invoice Date</dt>
                <dd className="text-sm text-gray-900">{formatDate(invoice.invoice_date)}</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Due Date</dt>
                <dd className="text-sm text-gray-900">{formatDate(invoice.due_date)}</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Payment Terms</dt>
                <dd className="text-sm text-gray-900 capitalize">
                  {invoice.payment_terms ? invoice.payment_terms.replace('_', ' ') : 'N/A'}
                </dd>
              </div>
              {invoice.days_overdue > 0 && (
                <div>
                  <dt className="text-sm text-gray-500">Days Overdue</dt>
                  <dd className="text-sm font-medium text-red-600">{invoice.days_overdue} days</dd>
                </div>
              )}
            </dl>
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-4">Customer Information</h3>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm text-gray-500">Customer</dt>
                <dd className="text-sm font-medium text-gray-900">{invoice.customer_name}</dd>
              </div>
              {invoice.customer_phone && (
                <div>
                  <dt className="text-sm text-gray-500">Phone</dt>
                  <dd className="text-sm text-gray-900">{invoice.customer_phone}</dd>
                </div>
              )}
              {invoice.customer_email && (
                <div>
                  <dt className="text-sm text-gray-500">Email</dt>
                  <dd className="text-sm text-gray-900">{invoice.customer_email}</dd>
                </div>
              )}
              {invoice.customer_address && (
                <div>
                  <dt className="text-sm text-gray-500">Address</dt>
                  <dd className="text-sm text-gray-900">{invoice.customer_address}</dd>
                </div>
              )}
            </dl>
          </div>
        </div>
      </div>

      {/* Amount Summary */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Amount Summary</h3>
        <dl className="space-y-3">
          <div className="flex justify-between">
            <dt className="text-sm text-gray-500">Subtotal</dt>
            <dd className="text-sm font-medium text-gray-900">
              {formatCurrency(invoice.subtotal)}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-sm text-gray-500">Tax</dt>
            <dd className="text-sm font-medium text-gray-900">
              {formatCurrency(invoice.tax_amount)}
            </dd>
          </div>
          <div className="flex justify-between pt-3 border-t border-gray-200">
            <dt className="text-base font-medium text-gray-900">Total Amount</dt>
            <dd className="text-base font-bold text-gray-900">
              {formatCurrency(invoice.total_amount)}
            </dd>
          </div>
          {parseFloat(invoice.amount_paid || 0) > 0 && (
            <div className="flex justify-between">
              <dt className="text-sm text-gray-500">Amount Paid</dt>
              <dd className="text-sm font-medium text-green-600">
                {formatCurrency(invoice.amount_paid)}
              </dd>
            </div>
          )}
          <div className="flex justify-between pt-3 border-t border-gray-200">
            <dt className="text-base font-medium text-gray-900">Balance Due</dt>
            <dd
              className={`text-base font-bold ${parseFloat(invoice.balance_due) > 0 ? 'text-red-600' : 'text-green-600'}`}
            >
              {formatCurrency(invoice.balance_due)}
            </dd>
          </div>
        </dl>
      </div>

      {/* Payment History */}
      {invoice.payments && invoice.payments.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Payment History</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Method
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Reference
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Recorded By
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {invoice.payments.map((payment) => (
                  <tr key={payment.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(payment.payment_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                      {payment.payment_method ? payment.payment_method.replace('_', ' ') : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {payment.reference_number || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-green-600">
                      {formatCurrency(payment.amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {payment.recorded_by_name}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Description & Notes */}
      {(invoice.description || invoice.notes) && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          {invoice.description && (
            <div className="mb-4">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Description</h3>
              <p className="text-sm text-gray-900">{invoice.description}</p>
            </div>
          )}
          {invoice.notes && (
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Notes</h3>
              <p className="text-sm text-gray-900 whitespace-pre-wrap">{invoice.notes}</p>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      {isAdmin && invoice.status !== 'paid' && invoice.status !== 'written_off' && (
        <div className="flex gap-3">
          <button
            onClick={() => navigate(`/accounting/invoices/${id}/payment`)}
            className="flex-1 bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg font-medium flex items-center justify-center gap-2"
          >
            <DollarSign className="h-5 w-5" />
            Record Payment
          </button>
        </div>
      )}
    </div>
  );
};

export default InvoiceDetailPage;
